// CAR-843: Self-hosted persistence layer for Excalidraw Collab.
// Replaces Firebase Firestore/Storage with HTTP calls to the self-hosted
// storage-backend (server.js under deploy/excalidraw/storage/).
//
// Scene data: stored/loaded via PUT/GET /api/v2/scenes/:roomId on the
// storage-backend filesystem, shared across all participants connecting to
// the same excalidraw-room. Late-joining participants and page reloads
// can load the same scene.
//
// File data (images): stored/loaded via PUT/GET /api/v2/collab/files/:prefix/:id.
//
// Encryption is client-side with the room key (same as the original Firebase
// implementation). The storage backend never sees plaintext scene data.

import { reconcileElements } from "@excalidraw/excalidraw";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/excalidraw/element/types";
import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import { getSceneVersion } from "@excalidraw/excalidraw/element";
import type Portal from "../collab/Portal";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { MIME_TYPES } from "@excalidraw/excalidraw/constants";
import type { SyncableExcalidrawElement } from ".";
import { getSyncableElements } from ".";
import type { Socket } from "socket.io-client";

// ── HTTP storage backend base URL ──────────────────────────────────────────

const BACKEND = import.meta.env.VITE_APP_BACKEND_V2_GET_URL || "";

// ── Scene version cache (socket-scoped, same pattern as original) ───────────

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => SceneVersionCache.cache.get(socket);
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

// ── Encryption helpers (client-side AES with roomKey) ──────────────────────

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);
  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  data: { iv: number[]; ciphertext: number[] },
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = new Uint8Array(data.ciphertext);
  const iv = new Uint8Array(data.iv);
  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

// ── Write-Token- und HMAC-Helper (CAR-843) ──────────────────────────────
//
// Der Write-Token ist SHA-256(roomKey). Er dient als HMAC-Secret fuer
// die X-Integrity-Signatur und wird als X-Write-Token an das Storage-
// Backend gesendet. Der AES-Encryption-Key (roomKey) selbst verlaesst
// den Client NIEMALS.
async function sha256Hex(input: string): Promise<string> {
  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function hmacHex(
  key: string,
  method: string,
  path: string,
  roomId: string,
  body: string | Uint8Array,
): Promise<string> {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const parts = [
    enc.encode(method),
    enc.encode(path),
    enc.encode(roomId),
    typeof body === "string" ? enc.encode(body) : body,
  ];
  const totalLen = parts.reduce((s, b) => s + b.byteLength, 0);
  const combined = new Uint8Array(totalLen);
  let off = 0;
  for (const p of parts) {
    combined.set(new Uint8Array(p), off);
    off += p.byteLength;
  }

  const sig = await crypto.subtle.sign("HMAC", cryptoKey, combined);
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ── Exported API ───────────────────────────────────────────────────────────

export const loadFirebaseStorage = async () => null;

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  return true;
};

// ── File persistence (images, etc.) via self-hosted storage ────────────────

export const saveFilesToFirebase = async ({
  prefix,
  files,
  roomKey,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
  roomKey: string;
}) => {
  const writeToken = await sha256Hex(roomKey);
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const path = `/api/v2/collab/files/${encodeURIComponent(prefix)}/${encodeURIComponent(id)}`;
        const headers: Record<string, string> = {};
        const integrity = await hmacHex(writeToken, "PUT", path, prefix, buffer);
        headers["X-Write-Token"] = writeToken;
        headers["X-Integrity"] = integrity;
        const response = await fetch(
          `${BACKEND}collab/files/${encodeURIComponent(prefix)}/${encodeURIComponent(id)}`,
          { method: "PUT", body: buffer, headers },
        );
        if (response.ok) {
          savedFiles.push(id);
        } else {
          erroredFiles.push(id);
        }
      } catch (error: any) {
        erroredFiles.push(id);
        console.error(error);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

// ── Scene persistence via self-hosted storage ──────────────────────────────

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  _appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  let storedElements = getSyncableElements(elements);
  const { ciphertext, iv } = await encryptElements(roomKey, storedElements);

  let payload = JSON.stringify({
    sceneVersion: getSceneVersion(storedElements),
    iv: Array.from(iv),
    ciphertext: Array.from(new Uint8Array(ciphertext)),
  });

  // Write-Token einmalig berechnen (deterministisch, kein Retry noetig)
  const writeToken = await sha256Hex(roomKey);

  // ── GET → reconcile → PUT mit ETag-CAS (Retry bis 3x) ────────────────
  const MAX_RETRIES = 3;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    let etag: string | undefined;
    try {
      const curResp = await fetch(
        `${BACKEND}scenes/${encodeURIComponent(roomId)}`,
      );
      if (curResp.ok) {
        etag = curResp.headers.get("ETag") ?? undefined;
        const curPayload = await curResp.json();
        const prevElements = getSyncableElements(
          restoreElements(await decryptElements(curPayload, roomKey), null),
        );
        if (getSceneVersion(prevElements) !== getSceneVersion(elements)) {
          storedElements = getSyncableElements(
            reconcileElements(
              elements,
              prevElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
              _appState,
            ),
          );
          const reEnc = await encryptElements(roomKey, storedElements);
          payload = JSON.stringify({
            sceneVersion: getSceneVersion(storedElements),
            iv: Array.from(reEnc.iv),
            ciphertext: Array.from(new Uint8Array(reEnc.ciphertext)),
          });
        }
      } else if (curResp.status !== 404) {
        // GET-Fehler ungleich 404: nicht stillschweigend PUT ohne If-Match
        console.warn("Scene load failed:", curResp.status);
        return null;
      }
    } catch {
      // Netzwerkfehler beim GET: nicht stillschweigend PUT ohne If-Match
      console.warn("Scene load network error");
      return null;
    }

    const integrity = await hmacHex(
      writeToken, "PUT",
      `/api/v2/scenes/${encodeURIComponent(roomId)}`,
      roomId, payload,
    );
    const headers: Record<string, string> = {
      "X-Write-Token": writeToken,
      "X-Integrity": integrity,
    };
    if (etag) headers["If-Match"] = etag;
    const response = await fetch(
      `${BACKEND}scenes/${encodeURIComponent(roomId)}`,
      { method: "PUT", body: payload, headers },
    );
    if (response.ok) break;
    if (response.status === 409 && attempt < MAX_RETRIES - 1) continue;
    console.warn("Scene save rejected by storage backend:", response.status);
    return null;
  }

  SceneVersionCache.set(socket, storedElements);
  return storedElements;
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  try {
    const response = await fetch(
      `${BACKEND}scenes/${encodeURIComponent(roomId)}`,
    );
    if (!response.ok) {
      return null;
    }
    const payload = await response.json();
    const elements = getSyncableElements(
      restoreElements(await decryptElements(payload, roomKey), null),
    );

    if (socket) {
      SceneVersionCache.set(socket, elements);
    }

    return elements;
  } catch {
    return null;
  }
};

// ── File loading (images) via self-hosted storage ─────────────────────────

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(
          `${BACKEND}collab/files/${encodeURIComponent(prefix)}/${encodeURIComponent(id)}`,
        );
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
